import { apiRequest } from '@/lib/http';

/**
 * Chamadas de autenticação. Nenhuma resposta traz o refresh token: ele vive só no cookie
 * httpOnly (ADR-003), e é por isso que não existe campo para ele aqui.
 */

export type MembershipRole = 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';

export interface OrganizationSummary {
  id: string;
  name: string;
  role: MembershipRole;
}

export interface SessionUser {
  id: string;
  name: string;
  email: string;
}

/**
 * Sessão de empresa ou de plataforma (ADR-027). Na de plataforma, `platformRole` vem
 * preenchido, `organization` é nula e não há permissão de tenant: a tela abre o painel.
 */
export interface AuthenticatedPayload {
  accessToken: string;
  user: SessionUser;
  organization: OrganizationSummary | null;
  organizations: OrganizationSummary[];
  permissions: string[];
  platformRole: 'ADMIN' | null;
}

/**
 * A primeira etapa pode já abrir a sessão (`NONE`) ou pedir o segundo fator: `TOTP` para
 * quem tem 2FA, `SETUP_2FA` para administrador que ainda precisa configurar.
 */
export type LoginResult =
  | ({ challenge: 'NONE' } & AuthenticatedPayload)
  | {
      challenge: 'TOTP' | 'SETUP_2FA';
      challengeToken: string;
      /** Dias de "confiar neste dispositivo"; 0 = opção desligada no servidor. */
      trustDeviceDays: number;
    };

export function login(email: string, password: string): Promise<LoginResult> {
  return apiRequest<LoginResult>('/auth/login', {
    method: 'POST',
    body: { email, password },
    skipAuth: true,
  });
}

export function refreshSession(): Promise<AuthenticatedPayload> {
  return apiRequest<AuthenticatedPayload>('/auth/refresh', { method: 'POST', skipAuth: true });
}

export function logout(): Promise<void> {
  return apiRequest<void>('/auth/logout', { method: 'POST', skipAuth: true });
}

export function switchOrganization(organizationId: string): Promise<AuthenticatedPayload> {
  return apiRequest<AuthenticatedPayload>('/auth/switch-organization', {
    method: 'POST',
    body: { organizationId },
  });
}

export function verifyTwoFactor(
  challengeToken: string,
  code: string,
  trustDevice = false,
): Promise<AuthenticatedPayload> {
  return apiRequest('/auth/2fa/verify', {
    method: 'POST',
    body: { challengeToken, code, trustDevice },
    skipAuth: true,
  });
}

export function verifyRecoveryCode(
  challengeToken: string,
  recoveryCode: string,
): Promise<AuthenticatedPayload> {
  return apiRequest('/auth/2fa/recovery', {
    method: 'POST',
    body: { challengeToken, recoveryCode },
    skipAuth: true,
  });
}

export interface TwoFactorSecret {
  secret: string;
  otpauthUri: string;
}

export function startRequiredSetup(challengeToken: string): Promise<TwoFactorSecret> {
  return apiRequest('/auth/2fa/setup', {
    method: 'POST',
    body: { challengeToken },
    skipAuth: true,
  });
}

export function confirmRequiredSetup(
  challengeToken: string,
  code: string,
  trustDevice = false,
): Promise<AuthenticatedPayload & { recoveryCodes: string[] }> {
  return apiRequest('/auth/2fa/setup/confirm', {
    method: 'POST',
    body: { challengeToken, code, trustDevice },
    skipAuth: true,
  });
}

export interface AccountSecurity {
  twoFactorEnabled: boolean;
  twoFactorRequired: boolean;
  recoveryCodesRemaining: number;
}

export function getSecurity(): Promise<AccountSecurity> {
  return apiRequest('/auth/security');
}

export function startOptionalEnable(): Promise<TwoFactorSecret & { challengeToken: string }> {
  return apiRequest('/auth/2fa/enable', { method: 'POST' });
}

export function confirmOptionalEnable(
  challengeToken: string,
  code: string,
): Promise<{ recoveryCodes: string[] }> {
  return apiRequest('/auth/2fa/enable/confirm', { method: 'POST', body: { challengeToken, code } });
}

export function disableTwoFactor(code: string): Promise<void> {
  return apiRequest('/auth/2fa/disable', { method: 'POST', body: { code } });
}

export function forgotPassword(email: string): Promise<void> {
  return apiRequest('/auth/password/forgot', { method: 'POST', body: { email }, skipAuth: true });
}

export function resetPassword(token: string, password: string): Promise<void> {
  return apiRequest('/auth/password/reset', {
    method: 'POST',
    body: { token, password },
    skipAuth: true,
  });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiRequest('/auth/password/change', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
}

export const securityKeys = {
  all: ['security'] as const,
  trustedDevices: () => [...securityKeys.all, 'trusted-devices'] as const,
};

/** Navegador que dispensa o código do 2FA (ADR-026). */
export interface TrustedDevice {
  id: string;
  label: string;
  ip: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  current: boolean;
}

export function listTrustedDevices(): Promise<TrustedDevice[]> {
  return apiRequest('/auth/trusted-devices');
}

export function revokeTrustedDevice(id: string): Promise<void> {
  return apiRequest(`/auth/trusted-devices/${id}`, { method: 'DELETE' });
}

export function revokeAllTrustedDevices(): Promise<void> {
  return apiRequest('/auth/trusted-devices', { method: 'DELETE' });
}
